import { useState, useEffect, useRef, useCallback } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/contexts/auth-context';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { Building2, Users, Loader2, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function PermissionsSelector({ selected_companies, onChange }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [subsidiaries, setSubsidiaries] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [isLoadingSubs, setIsLoadingSubs] = useState(false);
    const [isLoadingCollabs, setIsLoadingCollabs] = useState(false);

    const companyId = user?.company_uniq_id ?? user?.company?.uniq_id ?? null;
    const companyName = user?.company_name ?? user?.company?.name ?? t('procedures:monEntreprise');
    const currentUserId = user?.uniq_id ?? user?.id ?? null;
    const isSuperAdmin = user?.role === 'admin';

    // --- Super Admin: pagination state ---
    const [adminCompanies, setAdminCompanies] = useState([]);
    const [adminPage, setAdminPage] = useState(1);
    const [adminPageSize] = useState(20);
    const [adminPageCount, setAdminPageCount] = useState(1);
    const [adminTotal, setAdminTotal] = useState(0);
    const [subsidiariesLength, setSubsidiariesLength] = useState(0);
    const [adminSearch, setAdminSearch] = useState('');
    const [adminSearchApi, setAdminSearchApi] = useState('');
    const [isLoadingAdmin, setIsLoadingAdmin] = useState(false);
    const adminSearchTimeout = useRef(null);

    // Charger les sous-comptes
    useEffect(() => {
        if (!companyId) return;
        setIsLoadingSubs(true);
        horodatyApi.subsidiaries
            .list(companyId)
            .then((res) => {
                const list = res?.data ?? res?.data?.data ?? [];
                setSubsidiaries(Array.isArray(list) ? list : []);
            })
            .catch(() => setSubsidiaries([]))
            .finally(() => setIsLoadingSubs(false));
    }, [companyId]);

    // Super Admin : charger tous les sous-comptes (arborescence complète)
    const fetchAdminCompanies = useCallback(async () => {
        if (!isSuperAdmin || !companyId) return;
        setIsLoadingAdmin(true);
        try {
            const res = await horodatyApi.subsidiaries.list(companyId, {
                include_descendants: true,
                paginate: true,
                page: adminPage,
                per_page: adminPageSize,
                ...(adminSearchApi ? { search: adminSearchApi } : {}),
            });
            const list = res?.data?.data ?? [];
            const meta = res?.data?.meta;
            setAdminCompanies(Array.isArray(list) ? list : []);
            if (meta) {
                setAdminPageCount(meta.last_page ?? 1);
                setAdminTotal(meta.total ?? 0);
                if (!adminSearchApi) {
                    setSubsidiariesLength(meta.total ?? 0);
                }
            }
        } catch {
            setAdminCompanies([]);
        } finally {
            setIsLoadingAdmin(false);
        }
    }, [isSuperAdmin, adminPage, adminPageSize, adminSearchApi, companyId]);

    useEffect(() => {
        fetchAdminCompanies();
    }, [fetchAdminCompanies]);

    const handleAdminSearchChange = (value) => {
        setAdminSearch(value);
        if (adminSearchTimeout.current) clearTimeout(adminSearchTimeout.current);
        adminSearchTimeout.current = setTimeout(() => {
            setAdminSearchApi(value);
            setAdminPage(1);
        }, 400);
    };

    // Trouver la permission de l'entreprise du user (si cochée)
    const ownCompanyPerm = selected_companies.find((p) => p.company_id === companyId);
    const isOwnCompanyChecked = Boolean(ownCompanyPerm);

    // Charger les collaborateurs quand on coche notre entreprise
    useEffect(() => {
        if (!isOwnCompanyChecked || !companyId) {
            setCollaborators([]);
            return;
        }
        setIsLoadingCollabs(true);
        horodatyApi
            .managerUsers()
            .then((res) => {
                const list = res?.data?.data ?? res?.data ?? [];
                setCollaborators(Array.isArray(list) ? list : []);
            })
            .catch(() => setCollaborators([]))
            .finally(() => setIsLoadingCollabs(false));
    }, [isOwnCompanyChecked, companyId]);

    const toggleCompany = (companyUniqId, isOwn = false) => {
        const exists = selected_companies.find((p) => p.company_id === companyUniqId);
        if (exists) {
            // Décocher
            onChange(selected_companies.filter((p) => p.company_id !== companyUniqId));
        } else {
            // Cocher — si c'est notre entreprise, on n'ajoute pas user_ids (= tous par défaut)
            onChange([...selected_companies, { company_id: companyUniqId }]);
        }
    };

    const toggleAllCollaborators = (checked) => {
        if (!ownCompanyPerm) return;
        const updated = selected_companies.map((p) => {
            if (p.company_id !== companyId) return p;
            if (checked) {
                // Tous sélectionnés → retirer user_ids (convention: absent = tous)
                const { user_ids, ...rest } = p;
                return rest;
            } else {
                // Désélectionner tous sauf l'utilisateur courant (toujours présent)
                return { ...p, user_ids: currentUserId ? [currentUserId] : [] };
            }
        });
        onChange(updated);
    };

    const toggleCollaborator = (userUniqId) => {
        if (!ownCompanyPerm) return;
        // L'utilisateur courant ne peut pas être désélectionné
        if (userUniqId === currentUserId) return;
        const currentUserIds = ownCompanyPerm.user_ids; // undefined = tous

        let newUserIds;
        if (!currentUserIds) {
            // "Tous" est sélectionné → on décoche un seul → on crée la liste sans lui
            // On s'assure que l'utilisateur courant reste toujours présent
            newUserIds = collaborators.map((c) => c.uniq_id ?? c.id).filter((id) => id !== userUniqId);
            if (currentUserId && !newUserIds.includes(currentUserId)) {
                newUserIds = [currentUserId, ...newUserIds];
            }
        } else if (currentUserIds.includes(userUniqId)) {
            // Retirer
            newUserIds = currentUserIds.filter((id) => id !== userUniqId);
        } else {
            // Ajouter
            newUserIds = [...currentUserIds, userUniqId];
        }

        // Si tous sont sélectionnés, revenir à "tous" (pas de user_ids)
        const allIds = collaborators.map((c) => c.uniq_id ?? c.id);
        const allSelected = allIds.length > 0 && allIds.every((id) => newUserIds.includes(id));

        const updated = selected_companies.map((p) => {
            if (p.company_id !== companyId) return p;
            if (allSelected) {
                const { user_ids, ...rest } = p;
                return rest;
            }
            return { ...p, user_ids: newUserIds };
        });
        onChange(updated);
    };

    const isCollaboratorSelected = (userUniqId) => {
        if (!ownCompanyPerm) return false;
        if (!ownCompanyPerm.user_ids) return true; // tous sélectionnés
        return ownCompanyPerm.user_ids.includes(userUniqId);
    };

    const areAllCollaboratorsSelected = isOwnCompanyChecked && !ownCompanyPerm.user_ids;

    // Super Admin : Tout cocher / Tout décocher (tous les sous-comptes)
    const handleCheckAllCompanies = async () => {
        if (!companyId) return;
        try {
            const res = await horodatyApi.subsidiaries.list(companyId, {
                include_descendants: true,
                paginate: true,
                page: 1,
                per_page: 100,
            });
            const meta = res?.data?.meta;
            const totalPages = meta?.last_page ?? 1;
            let allIds = (res?.data?.data ?? []).map((c) => c.uniq_id ?? c.id);

            // Charger toutes les pages restantes
            if (totalPages > 1) {
                const pagePromises = [];
                for (let p = 2; p <= totalPages; p++) {
                    pagePromises.push(
                        horodatyApi.subsidiaries.list(companyId, {
                            include_descendants: true,
                            paginate: true,
                            page: p,
                            per_page: 100,
                        }),
                    );
                }
                const pages = await Promise.all(pagePromises);
                for (const pageRes of pages) {
                    const pageData = pageRes?.data?.data ?? [];
                    allIds = [...allIds, ...pageData.map((c) => c.uniq_id ?? c.id)];
                }
            }

            const newSelections = allIds
                .filter((id) => !selected_companies.some((p) => p.company_id === id))
                .map((id) => ({ company_id: id }));
            onChange([...selected_companies, ...newSelections]);
        } catch {
            // En cas d'erreur, cocher uniquement la page courante
            const currentIds = adminCompanies.map((c) => c.uniq_id ?? c.id);
            const newSelections = currentIds
                .filter((id) => !selected_companies.some((p) => p.company_id === id))
                .map((id) => ({ company_id: id }));
            onChange([...selected_companies, ...newSelections]);
        }
    };

    const handleUncheckAllCompanies = () => {
        // Retirer toutes les entreprises sauf l'entreprise propre du user
        onChange(selected_companies.filter((p) => p.company_id === companyId));
    };

    // Compter les entreprises externes sélectionnées
    const selectedExternalCount = selected_companies.filter((p) => p.company_id !== companyId).length;

    return (
        <div>
            <div className="bg-card rounded-[12px] border border-border divide-y divide-border">
                {/* Mon entreprise */}
                <div>
                    <label className="flex items-center gap-[12px] px-[16px] py-[14px] cursor-pointer">
                        <Checkbox
                            checked={isOwnCompanyChecked}
                            onCheckedChange={() => toggleCompany(companyId, true)}
                        />
                        <Building2 className="h-4 w-4 text-foreground" />
                        <span className="text-[14px] text-foreground font-medium">{companyName}</span>
                        <span className="text-[11px] text-muted-foreground ml-auto">{t('procedures:monEntreprise')}</span>
                    </label>

                    {/* Liste des collaborateurs (visible seulement si entreprise cochée) */}
                    {isOwnCompanyChecked && (
                        <div className="px-[16px] pb-[12px]">
                            {isLoadingCollabs ? (
                                <div className="flex items-center gap-[8px] py-[8px] text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className="text-[12px]">{t('procedures:chargementDesCollaborateurs')}</span>
                                </div>
                            ) : (
                                <div className="bg-muted rounded-[8px] p-[12px] space-y-[8px]">
                                    {/* Tous sélectionner */}
                                    <label className="flex items-center gap-[10px] cursor-pointer pb-[8px] border-b border-border">
                                        <Checkbox
                                            checked={areAllCollaboratorsSelected}
                                            onCheckedChange={toggleAllCollaborators}
                                        />
                                        <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-[13px] text-foreground font-medium">
                                            {t('procedures:tousLesCollaborateurs')}
                                        </span>
                                        <span className="text-[11px] text-muted-foreground ml-auto">
                                            {areAllCollaboratorsSelected
                                                ? collaborators.length
                                                : (ownCompanyPerm?.user_ids?.length ?? 0)}
                                            /{collaborators.length}
                                        </span>
                                    </label>
                                    {/* Liste individuelle */}
                                    <div className="max-h-[200px] overflow-y-auto space-y-[4px] scrollbar-hide">
                                        {collaborators.map((collab) => {
                                            const collabId = collab.uniq_id ?? collab.id;
                                            const isCurrentUser = collabId === currentUserId;
                                            return (
                                                <label
                                                    key={collabId}
                                                    className={`flex items-center gap-[10px] py-[6px] px-[4px] rounded-[6px] ${
                                                        isCurrentUser
                                                            ? 'opacity-60 cursor-not-allowed'
                                                            : 'cursor-pointer hover:bg-card'
                                                    }`}
                                                >
                                                    <Checkbox
                                                        checked={isCollaboratorSelected(collabId)}
                                                        onCheckedChange={() => toggleCollaborator(collabId)}
                                                        disabled={isCurrentUser}
                                                    />
                                                    <span className="text-[13px] text-foreground">
                                                        {collab.first_name ?? collab.firstname}{' '}
                                                        {collab.last_name ?? collab.lastname}
                                                    </span>
                                                    {isCurrentUser && (
                                                        <span className="text-[11px] text-muted-foreground ml-auto">
                                                            {t('procedures:vous')}
                                                        </span>
                                                    )}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Sous-comptes directs (non super admin) */}
                {!isSuperAdmin &&
                    (isLoadingSubs ? (
                        <div className="flex items-center gap-[8px] px-[16px] py-[14px] text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-[12px]">{t('procedures:chargementDesSousComptes')}</span>
                        </div>
                    ) : subsidiaries.length === 0 ? (
                        <div className="px-[16px] py-[10px]">
                            <p className="text-[12px] text-muted-foreground italic">{t('procedures:aucunSousCompte')}</p>
                        </div>
                    ) : (
                        subsidiaries.map((sub) => {
                            const subId = sub.uniq_id ?? sub.id;
                            const isChecked = selected_companies.some((p) => p.company_id === subId);
                            return (
                                <label
                                    key={subId}
                                    className="flex items-center gap-[12px] px-[16px] py-[14px] cursor-pointer"
                                >
                                    <Checkbox checked={isChecked} onCheckedChange={() => toggleCompany(subId)} />
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-[14px] text-foreground">{sub.name}</span>
                                </label>
                            );
                        })
                    ))}

                {/* Super Admin : Liste paginée de tous les sous-comptes (arborescence complète) */}
                {isSuperAdmin && (
                    <div className="px-[16px] py-[12px] space-y-[10px]">
                        {/* Header */}
                        <div className="flex items-center gap-[8px]">
                            <Building2 className="h-4 w-4 text-foreground" />
                            <span className="text-[14px] text-foreground font-medium">{t('procedures:sousComptes')}</span>
                            <span className="text-[11px] text-muted-foreground ml-auto">
                                {selectedExternalCount}/{subsidiariesLength}
                            </span>
                        </div>

                        {/* Barre de recherche */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder={t('procedures:rechercherUnSousCompte')}
                                value={adminSearch ?? ''}
                                onChange={(e) => handleAdminSearchChange(e.target.value)}
                                className="w-full pl-10 pr-10 py-2 bg-muted border border-border rounded-lg text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary"
                            />
                            {adminSearch && (
                                <button
                                    type="button"
                                    onClick={() => handleAdminSearchChange('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-border transition-colors"
                                >
                                    <X className="w-4 h-4 text-muted-foreground" />
                                </button>
                            )}
                        </div>

                        {/* Boutons Tout cocher / Tout décocher */}
                        <div className="flex items-center gap-[8px]">
                            <Button
                                type="button"
                                variant="outline"
                                color="neutral"
                                size="xsm"
                                onClick={handleCheckAllCompanies}
                                className="text-[12px]"
                            >
                                {t('procedures:toutCocher')}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                color="neutral"
                                size="xsm"
                                onClick={handleUncheckAllCompanies}
                                className="text-[12px]"
                            >
                                {t('procedures:toutDecocher')}
                            </Button>
                        </div>

                        {/* Liste des entreprises */}
                        {isLoadingAdmin ? (
                            <div className="flex items-center gap-[8px] py-[8px] text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-[12px]">{t('procedures:chargementDesSousComptes')}</span>
                            </div>
                        ) : adminCompanies.length === 0 ? (
                            <p className="text-[12px] text-muted-foreground italic py-[8px]">
                                {adminSearchApi ? t('procedures:aucunSousCompteTrouve') : t('procedures:aucunSousCompte')}
                            </p>
                        ) : (
                            <div className="space-y-0 divide-y divide-border rounded-[8px] border border-border overflow-hidden">
                                {adminCompanies.map((company) => {
                                    const cId = company.uniq_id ?? company.id;
                                    const isChecked = selected_companies.some((p) => p.company_id === cId);
                                    return (
                                        <label
                                            key={cId}
                                            className="flex items-center gap-[12px] px-[12px] py-[10px] cursor-pointer hover:bg-muted/50 transition-colors"
                                        >
                                            <Checkbox checked={isChecked} onCheckedChange={() => toggleCompany(cId)} />
                                            <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            <span className="text-[13px] text-foreground truncate">{company.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        {/* Pagination */}
                        {adminPageCount > 1 && (
                            <div className="flex items-center justify-between pt-[4px]">
                                <span className="text-[12px] text-muted-foreground">
                                    {t('procedures:page')} {adminPage}/{adminPageCount}
                                </span>
                                <div className="flex items-center gap-[4px]">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        color="neutral"
                                        size="auto"
                                        className="h-7 w-7"
                                        disabled={adminPage <= 1}
                                        onClick={() => setAdminPage((p) => Math.max(1, p - 1))}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        color="neutral"
                                        size="auto"
                                        className="h-7 w-7"
                                        disabled={adminPage >= adminPageCount}
                                        onClick={() => setAdminPage((p) => Math.min(adminPageCount, p + 1))}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
